import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { systems, levels } from "@/mocks/systems";
import { articles } from "@/mocks/articles";

const categories = ["全部", "炼丹", "针灸", "功法", "总览"] as const;

export default function Xiulian() {
  const [active, setActive] = useState<(typeof categories)[number]>("全部");

  const list = useMemo(
    () => (active === "全部" ? articles : articles.filter((a) => a.category === active)),
    [active],
  );

  return (
    <>
      <Navbar />
      <BackToTop />

      <section className="bg-primary-700 pt-24 pb-12 md:pt-28 md:pb-16">
        <div className="container-site">
          <nav aria-label="面包屑" className="text-xs text-primary-300">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-accent-300">修炼体系</li>
            </ol>
          </nav>
          <h1 className="mt-4 text-2xl font-bold text-background-50 md:text-4xl">天才医仙漫画修炼体系</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
            炼丹、针灸、功法三大体系并行运作，共同支撑主角由凡入仙。这里集中整理了体系的分类解读与六大境界的完整脉络。
          </p>
        </div>
      </section>

      {/* 三大体系 */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-site grid gap-6 md:grid-cols-3">
          {systems.map((s) => (
            <article key={s.key} className="overflow-hidden rounded-lg border border-background-200/80 bg-background-100/60">
              <div className="relative h-44 w-full overflow-hidden">
                <img src={s.image} alt={`天才医仙漫画${s.name}体系设定图`} className="h-full w-full object-cover object-top" />
                <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-md bg-background-50/90 text-primary-700">
                  <i className={`${s.icon} text-xl`} aria-hidden="true"></i>
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-lg font-bold">{s.name}</h2>
                  <span className="text-xs font-medium text-accent-700">{s.subtitle}</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
                <ul className="mt-4 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-fill mt-0.5 text-primary-600" aria-hidden="true"></i>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* 境界脉络 */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="container-site">
          <div className="max-w-2xl">
            <p className="text-sm font-medium uppercase tracking-wider text-primary-600">境界脉络</p>
            <h2 className="mt-2 text-2xl font-bold md:text-3xl">六大境界的晋级顺序</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              每一境界都是医术与修为的双重跃升，环环相扣，构成医仙之路的主轴。
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {levels.map((lv) => (
              <div key={lv.order} className="flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-lg font-bold text-foreground-950">
                  {lv.order}
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-primary-800">{lv.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{lv.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 分类文章 */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-site">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-medium uppercase tracking-wider text-primary-600">专题文章</p>
              <h2 className="mt-2 text-2xl font-bold md:text-3xl">按流派浏览解读</h2>
            </div>
            <div role="tablist" aria-label="流派筛选" className="inline-flex w-fit gap-1 rounded-full bg-background-200/70 p-1">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={active === c}
                  onClick={() => setActive(c)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    active === c
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-700 hover:bg-background-300/60"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <Link
                key={a.id}
                to={`/article/${a.id}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-100/60 transition-transform hover:-translate-y-1"
              >
                <div className="relative h-40 w-full overflow-hidden">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-0.5 text-xs font-medium text-foreground-950">
                    {a.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-heading text-base font-bold leading-snug group-hover:text-primary-700">{a.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-sparkling-fill text-primary-500" aria-hidden="true"></i>
                      {a.level}
                    </span>
                    <span>{a.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {list.length === 0 && (
            <p className="mt-10 text-center text-sm text-foreground-500">该分类下暂无内容，去看看其他流派吧。</p>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}